import {
  Button,
  Form,
  FormProps,
  Input,
  InputNumber,
  Modal,
  Select,
  Table,
  Spin,
} from "antd";
import { Plus, Save, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { CustomizeRequiredMark } from "~/components/common/custom-required-mark";
import { useNotification } from "~/hooks";
import { useGetSupplies } from "~/services/supply";
import {
  CreateSupplyKitParams,
  SUPPLY_KIT_KEYS,
  useCreateSupplyKit,
  useGetSupplyKit,
  useUpdateSupplyKit,
  SupplyKitItemInput,
} from "~/services/supply-kit";
import { useSupplyKitStore } from "./supply-kit.store";
import { useQueryClient } from "@tanstack/react-query";

export const ModalSupplyKit = () => {
  const qc = useQueryClient();
  const [form] = Form.useForm<CreateSupplyKitParams>();
  const { formState, closeKitModal } = useSupplyKitStore();
  const [selectedItems, setSelectedItems] = useState<SupplyKitItemInput[]>([]);
  const [selectedSupplyId, setSelectedSupplyId] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(1);

  const { data: supplies, isLoading: isLoadingSupplies } = useGetSupplies({
    page: 1,
    pageSize: 500,
  });

  const { data: kit, isLoading: isLoadingKit } = useGetSupplyKit(
    formState.kitId
  );

  const { success, error } = useNotification();
  const createMutation = useCreateSupplyKit();
  const updateMutation = useUpdateSupplyKit();

  useEffect(() => {
    if (!kit) return;
    form.setFieldsValue({
      name: kit.name,
      description: kit.description,
    });
    setSelectedItems(
      kit.items.map((item) => ({
        supplyId: item.supplyId,
        quantity: item.quantity,
      }))
    );
  }, [kit, form]);

  const handleClose = () => {
    form.resetFields();
    setSelectedItems([]);
    setSelectedSupplyId("");
    setQuantity(1);
  };

  const handleAddItem = () => {
    if (!selectedSupplyId) return;

    const existingIndex = selectedItems.findIndex(
      (item) => item.supplyId === selectedSupplyId
    );

    if (existingIndex >= 0) {
      const newItems = [...selectedItems];
      newItems[existingIndex].quantity += quantity;
      setSelectedItems(newItems);
    } else {
      setSelectedItems([
        ...selectedItems,
        { supplyId: selectedSupplyId, quantity },
      ]);
    }

    setSelectedSupplyId("");
    setQuantity(1);
  };

  const handleRemoveItem = (supplyId: string) => {
    setSelectedItems(selectedItems.filter((item) => item.supplyId !== supplyId));
  };

  const handleSubmit: FormProps["onFinish"] = async (values) => {
    if (selectedItems.length === 0) {
      error({
        message: "No items",
        description: "Please add at least one supply item to the kit.",
      });
      return;
    }

    const payload = {
      ...values,
      items: selectedItems,
    };

    const mutation = formState.kitId ? updateMutation : createMutation;
    const mutationPayload = formState.kitId
      ? { id: formState.kitId, ...payload }
      : payload;

    mutation.mutate(mutationPayload, {
      onSuccess: () => {
        success({
          message: "Kit saved successfully",
          description: `The supply kit has been ${formState.kitId ? "updated" : "created"}.`,
        });
        closeKitModal();
        handleClose();
        qc.invalidateQueries({
          queryKey: SUPPLY_KIT_KEYS.GET_SUPPLY_KITS({ page: 1, pageSize: 20 }),
        });
      },
      onError: (err) => {
        error({
          message: "Error saving kit",
          description: err?.message || "An error occurred.",
        });
      },
    });
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  const columns = [
    {
      title: "Supply Item",
      dataIndex: "supplyId",
      key: "supplyId",
      render: (supplyId: string) => {
        const supply = supplies?.items.find((s) => s.id === supplyId);
        return supply?.name || supplyId;
      },
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: unknown, record: SupplyKitItemInput) => (
        <Button
          type="text"
          danger
          icon={<Trash2 size={16} />}
          onClick={() => handleRemoveItem(record.supplyId)}
        >
          Remove
        </Button>
      ),
    },
  ];

  return (
    <Modal
      open={formState.open}
      onCancel={() => {
        closeKitModal();
        handleClose();
      }}
      width="80%"
      destroyOnClose
      footer={null}
    >
      <Spin spinning={isLoadingKit} size="large">
        <Form
          form={form}
          size="large"
          layout="vertical"
          requiredMark={CustomizeRequiredMark}
          onFinish={handleSubmit}
        >
          <h1 className="text-2xl font-bold mb-6">
            {formState.kitId ? "Edit Supply Kit" : "Create Supply Kit"}
          </h1>

          <div className="bg-white rounded-lg w-full mb-6">
            <div className="space-y-4">
              <Form.Item
                name="name"
                label="Kit Name"
                rules={[
                  { required: true, message: "Please input the kit name!" },
                ]}
              >
                <Input placeholder="e.g., Drywall Kit" size="large" />
              </Form.Item>

              <Form.Item name="description" label="Description">
                <Input.TextArea
                  rows={3}
                  placeholder="Describe what this kit is used for..."
                  className="text-base"
                />
              </Form.Item>
            </div>
          </div>

          <div className="bg-white rounded-lg w-full border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Add Supplies to Kit</h3>

            <div className="grid grid-cols-12 gap-4 mb-4">
              <div className="col-span-7">
                <Select
                  showSearch
                  placeholder="Select a supply"
                  size="large"
                  value={selectedSupplyId || undefined}
                  onChange={setSelectedSupplyId}
                  loading={isLoadingSupplies}
                  optionFilterProp="label"
                  className="w-full"
                  options={supplies?.items.map((supply) => ({
                    label: `${supply.name} - ${supply.supplyRef}`,
                    value: supply.id,
                  }))}
                />
              </div>

              <div className="col-span-3">
                <InputNumber
                  placeholder="Quantity"
                  size="large"
                  min={1}
                  value={quantity}
                  onChange={(val) => setQuantity(val || 1)}
                  className="w-full"
                />
              </div>

              <div className="col-span-2">
                <Button
                  type="primary"
                  icon={<Plus size={16} />}
                  onClick={handleAddItem}
                  disabled={!selectedSupplyId}
                  size="large"
                  className="w-full"
                >
                  Add
                </Button>
              </div>
            </div>

            <Table
              dataSource={selectedItems}
              columns={columns}
              rowKey="supplyId"
              pagination={false}
              locale={{ emptyText: "No items added to kit yet" }}
            />
          </div>

          <div className="flex justify-end space-x-3">
            <Button
              htmlType="button"
              onClick={() => {
                closeKitModal();
                handleClose();
              }}
              className="px-4 py-2"
            >
              <X size={16} className="mr-2" />
              Cancel
            </Button>
            <Button
              loading={isLoading}
              htmlType="submit"
              type="primary"
              className="px-4 py-2"
            >
              <Save size={16} className="mr-2" />
              Save Kit
            </Button>
          </div>
        </Form>
      </Spin>
    </Modal>
  );
};
